import { useEffect } from 'react';

const noop = () => {};

const useThemeEffects = (currentTheme, enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    cleanupThemeEffects();

    let cleanupEffect = noop;

    switch (currentTheme) {
      case 'rainy-thunderbolt':
        cleanupEffect = createStorm();
        break;
      case 'winter-wonderland':
        createSnowflakes();
        break;
      case 'autumn-season':
        createFallingLeaves();
        break;
      case 'summer-on-beach':
        createSummerSun();
        break;
      case 'spring-bloom':
        createFallingPetals();
        break;
      case 'starry-night':
        createStarryNight();
        break;
      default:
        break;
    }

    return () => {
      cleanupEffect();
      cleanupThemeEffects();
    };
  }, [currentTheme, enabled]);
};

function cleanupThemeEffects() {
  const storm = document.getElementById('storm');
  const snowContainer = document.getElementById('snow-container');
  const leavesContainer = document.getElementById('leaves-container');
  const sunRaysContainer = document.getElementById('sun-rays-container');
  const petalsContainer = document.getElementById('petals-container');
  const starsContainer = document.getElementById('stars-container');
  const moonElement = document.querySelector('.moon');

  if (storm) {
    storm.remove();
  }

  if (snowContainer) {
    snowContainer.remove();
  }

  if (leavesContainer) {
    leavesContainer.remove();
  }

  if (sunRaysContainer) {
    sunRaysContainer.remove();
  }

  if (petalsContainer) {
    petalsContainer.remove();
  }

  if (starsContainer) {
    starsContainer.remove();
  }

  if (moonElement?.parentNode) {
    moonElement.parentNode.removeChild(moonElement);
  }
}

/**
 * Storm Watch.
 *
 * One system rather than two effects: sky, cloud mass, two curtains of rain
 * at different depths, splashes, and lightning that strikes on its own
 * schedule. All of it is markup plus CSS animation — no per-frame JavaScript,
 * so it costs nothing but compositing once it is up.
 */
function createStorm() {
  const existing = document.getElementById('storm');
  if (existing) {
    existing.remove();
  }

  const storm = document.createElement('div');
  storm.id = 'storm';
  storm.className = 'storm';
  // Decoration. Nothing here should reach a screen reader.
  storm.setAttribute('aria-hidden', 'true');

  const clouds = document.createElement('div');
  clouds.className = 'storm__clouds';
  clouds.innerHTML = '<i></i><i></i><i></i>';

  const sky = document.createElement('div');
  sky.className = 'storm__sky';

  const flash = document.createElement('div');
  flash.className = 'storm__flash';

  /*
     The channel. Drawn once and moved horizontally per strike rather than
     rebuilt, and stroked as segments of decreasing width — a bolt is widest
     where it leaves the cloud and thins to the ground. An even-width path
     with a wide symmetric zigzag is what makes a drawn bolt look drawn, so
     the deviations here are small and irregular and the forks are short and
     one-sided.
  */
  const CHANNEL = [
    [62, 0], [52, 42], [61, 72], [45, 114], [54, 144],
    [36, 192], [45, 224], [28, 274], [36, 302], [19, 352], [13, 400],
  ];
  const line = (pts) => `M${pts.map((p) => p.join(' ')).join(' L')}`;

  const taper = CHANNEL.slice(0, -1)
    .map((p1, i) => {
      const p2 = CHANNEL[i + 1];
      const w = (4.2 - i * 0.34).toFixed(2);
      return `<path d="${line([p1, p2])}" stroke-width="${w}"/>`;
    })
    .join('');

  const bolt = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  bolt.setAttribute('class', 'storm__bolt');
  bolt.setAttribute('viewBox', '0 0 120 400');
  bolt.setAttribute('preserveAspectRatio', 'xMidYMin meet');
  bolt.innerHTML =
    // A soft wide pass under the channel: the air around a strike glows too.
    `<path class="storm__bolt-glow" d="${line(CHANNEL)}"/>` +
    taper +
    `<path class="storm__bolt-branch" d="${line([[61, 72], [86, 104], [77, 112], [92, 142]])}"/>` +
    `<path class="storm__bolt-branch" d="${line([[45, 114], [24, 140], [32, 148], [17, 174]])}"/>` +
    `<path class="storm__bolt-branch" d="${line([[36, 192], [64, 230], [55, 236], [67, 260]])}"/>`;

  /** One curtain of rain. Far is thin, slow and dim; near is the opposite. */
  const curtain = (modifier, count, minLen, maxLen, minDur, maxDur, minOp, maxOp) => {
    const layer = document.createElement('div');
    layer.className = `storm__rain storm__rain--${modifier}`;

    // Built as one string: 130 appendChild calls each force their own style
    // recalculation, and this runs on every switch into the theme.
    let html = '';
    for (let i = 0; i < count; i += 1) {
      const left = Math.random() * 100;
      const len = minLen + Math.random() * (maxLen - minLen);
      const dur = minDur + Math.random() * (maxDur - minDur);
      const delay = Math.random() * dur;
      const op = minOp + Math.random() * (maxOp - minOp);
      html +=
        `<span class="storm__drop" style="left:${left.toFixed(2)}%;` +
        `height:${len.toFixed(0)}px;animation-duration:${dur.toFixed(2)}s;` +
        `animation-delay:-${delay.toFixed(2)}s;opacity:${op.toFixed(2)}"></span>`;
    }
    layer.innerHTML = html;
    return layer;
  };

  const splashes = document.createElement('div');
  splashes.className = 'storm__splashes';
  let splashHtml = '';
  for (let i = 0; i < 16; i += 1) {
    splashHtml +=
      `<span class="storm__splash" style="left:${(Math.random() * 100).toFixed(2)}%;` +
      `animation-delay:-${(Math.random() * 1.4).toFixed(2)}s"></span>`;
  }
  splashes.innerHTML = splashHtml;

  const mist = document.createElement('div');
  mist.className = 'storm__mist';

  storm.append(
    sky,
    clouds,
    flash,
    bolt,
    curtain('far', 90, 14, 34, 0.9, 1.7, 0.12, 0.3),
    curtain('near', 42, 55, 110, 0.42, 0.72, 0.3, 0.6),
    splashes,
    mist
  );
  document.body.appendChild(storm);

  const still =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (still) {
    // The stylesheet already stills every layer and hides the lightning.
    return () => storm.remove();
  }

  let disposed = false;
  const timers = new Set();

  const later = (fn, ms) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (!disposed) fn();
    }, ms);
    timers.add(id);
  };

  const flashLayer = flash;

  /*
     The strike ends when its animation ends, not when a timer says so.

     A timer would be wall-clock; the animation is what the viewer actually
     sees, and browsers throttle timers hard on a hidden tab — the class
     could sit on the element for a minute after the light had stopped.
  */
  const endStrike = () => storm.classList.remove('is-striking', 'is-sheet');
  flashLayer.addEventListener('animationend', endStrike);

  const strike = () => {
    // Restart the animations even if one is still running.
    endStrike();
    // Reading offsetWidth forces the removal to take effect before the class
    // goes back on; without it the browser coalesces both and nothing replays.
    void storm.offsetWidth;

    storm.style.setProperty('--bolt-x', `${(12 + Math.random() * 76).toFixed(1)}%`);
    // Most strikes are behind the cloud — a bolt every time reads as a
    // special effect rather than as weather.
    if (Math.random() < 0.62) {
      storm.classList.add('is-sheet');
    }
    storm.classList.add('is-striking');

    schedule();
  };

  function schedule() {
    later(strike, 4000 + Math.random() * 11000);
  }

  /*
     Nothing is scheduled while the tab is in the background. Without this the
     throttled timers come due all at once on return and the storm fires a
     burst of strikes to catch up.
  */
  const onVisibility = () => {
    if (document.hidden) {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
      endStrike();
    } else {
      schedule();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  if (!document.hidden) {
    later(strike, 1800);
  }

  return () => {
    disposed = true;
    document.removeEventListener('visibilitychange', onVisibility);
    flashLayer.removeEventListener('animationend', endStrike);
    timers.forEach((id) => window.clearTimeout(id));
    timers.clear();
    storm.remove();
  };
}

function createSnowflakes() {
  const existingSnowContainer = document.getElementById('snow-container');
  if (existingSnowContainer) {
    existingSnowContainer.remove();
  }

  const snowContainer = document.createElement('div');
  snowContainer.id = 'snow-container';
  snowContainer.className = 'snow-container';
  document.body.appendChild(snowContainer);

  const snowflakeCount = 100;
  for (let i = 0; i < snowflakeCount; i++) {
    const snowflake = document.createElement('div');
    snowflake.className = 'snowflake';

    const leftPos = Math.random() * 100;
    const animationDuration = 5 + Math.random() * 10;
    const animationDelay = Math.random() * 5;
    const opacity = 0.3 + Math.random() * 0.7;
    const size = 3 + Math.random() * 6;

    snowflake.style.left = `${leftPos}%`;
    snowflake.style.animationDuration = `${animationDuration}s`;
    snowflake.style.animationDelay = `${animationDelay}s`;
    snowflake.style.opacity = opacity;
    snowflake.style.width = `${size}px`;
    snowflake.style.height = `${size}px`;

    snowContainer.appendChild(snowflake);
  }
}

function createFallingLeaves() {
  const existingLeavesContainer = document.getElementById('leaves-container');
  if (existingLeavesContainer) {
    existingLeavesContainer.remove();
  }

  const leavesContainer = document.createElement('div');
  leavesContainer.id = 'leaves-container';
  leavesContainer.className = 'leaves-container';
  document.body.appendChild(leavesContainer);

  const leafCount = 80;
  for (let i = 0; i < leafCount; i++) {
    const leaf = document.createElement('div');
    leaf.className = 'leaf';

    const leftPos = Math.random() * 100;
    const animationDuration = 6 + Math.random() * 12;
    const animationDelay = Math.random() * 5;
    const opacity = 0.4 + Math.random() * 0.6;
    const size = 8 + Math.random() * 12;

    leaf.style.left = `${leftPos}%`;
    leaf.style.animationDuration = `${animationDuration}s`;
    leaf.style.animationDelay = `${animationDelay}s`;
    leaf.style.opacity = opacity;
    leaf.style.width = `${size}px`;
    leaf.style.height = `${size}px`;

    leavesContainer.appendChild(leaf);
  }
}

function createSummerSun() {
  const existingSunContainer = document.getElementById('sun-rays-container');
  if (existingSunContainer) {
    existingSunContainer.remove();
  }

  const sunRaysContainer = document.createElement('div');
  sunRaysContainer.id = 'sun-rays-container';
  sunRaysContainer.className = 'sun-rays-container';
  document.body.appendChild(sunRaysContainer);

  const sun = document.createElement('div');
  sun.className = 'sun';
  sunRaysContainer.appendChild(sun);
}

function createFallingPetals() {
  const existingPetalsContainer = document.getElementById('petals-container');
  if (existingPetalsContainer) {
    existingPetalsContainer.remove();
  }

  const petalsContainer = document.createElement('div');
  petalsContainer.id = 'petals-container';
  petalsContainer.className = 'petals-container';
  document.body.appendChild(petalsContainer);

  const petalCount = 80;
  for (let i = 0; i < petalCount; i++) {
    const petal = document.createElement('div');
    petal.className = 'petal';

    const leftPos = Math.random() * 100;
    const animationDuration = 10 + Math.random() * 15;
    const animationDelay = Math.random() * 5;
    const opacity = 0.4 + Math.random() * 0.6;
    const size = 8 + Math.random() * 10;
    const colorVariations = ['#ffb6c1', '#ffc0cb', '#ffb6c1', '#ff69b4', '#dda0dd'];
    const randomColor = colorVariations[Math.floor(Math.random() * colorVariations.length)];

    petal.style.left = `${leftPos}%`;
    petal.style.animationDuration = `${animationDuration}s`;
    petal.style.animationDelay = `${animationDelay}s`;
    petal.style.opacity = opacity;
    petal.style.width = `${size}px`;
    petal.style.height = `${size}px`;
    petal.style.backgroundColor = randomColor;

    petalsContainer.appendChild(petal);
  }
}

function createStarryNight() {
  const existingStarsContainer = document.getElementById('stars-container');
  if (existingStarsContainer) {
    existingStarsContainer.remove();
  }

  const starsContainer = document.createElement('div');
  starsContainer.id = 'stars-container';
  starsContainer.className = 'stars-container';
  document.body.appendChild(starsContainer);

  const starCount = 150;
  for (let i = 0; i < starCount; i++) {
    const star = document.createElement('div');
    star.className = 'star-night';

    const leftPos = Math.random() * 100;
    const topPos = Math.random() * 100;
    const animationDelay = Math.random() * 5;
    const opacity = 0.3 + Math.random() * 0.7;
    const size = 1 + Math.random() * 3;

    star.style.left = `${leftPos}%`;
    star.style.top = `${topPos}%`;
    star.style.animationDelay = `${animationDelay}s`;
    star.style.opacity = opacity;
    star.style.width = `${size}px`;
    star.style.height = `${size}px`;

    starsContainer.appendChild(star);
  }

  if (!document.querySelector('.moon')) {
    const moon = document.createElement('div');
    moon.className = 'moon';
    document.body.appendChild(moon);
  }
}

export default useThemeEffects;
